import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToasterService } from '@abp/ng.theme.shared';
import { LocalizationService } from '@abp/ng.core';
import { ConfigurationSettingsService } from '@proxy/configuration-settings/configuration-settings.service';
import { ConfigurationSettingsDto } from '@proxy/configuration-settings/models';
import { ProviderEnum } from '@proxy/subscriptions/provider-enum.enum';
import { RegionEnum } from '@proxy/subscriptions/region-enum.enum';
import { RegionProviderMappingService } from 'src/app/shared/services/region-provider-mapping.service';

@Component({
  selector: 'ldm-product-credential-settings',
  templateUrl: './product-credential-settings.component.html',
  styleUrls: ['./product-credential-settings.component.scss'],
})
export class ProductCredentialSettingsComponent implements OnInit {
  products = ['KnovvuSR', 'KnovvuBiometrics', 'KnovvuVirtualAgent', 'KnovvuAnalytics', 'KnovvuML'];
  regions = [
    { name: 'Turkey', value: RegionEnum.Turkey },
    { name: 'Paris', value: RegionEnum.Paris },
    { name: 'London', value: RegionEnum.London },
    { name: 'Dubai', value: RegionEnum.Dubai },
    { name: 'Ohio', value: RegionEnum.Ohio },
    { name: 'Jeddah', displayName: 'Jeddah', value: RegionEnum.Jeddah }
  ];
  activeTab = 'KnovvuSR';
  forms: { [product: string]: { [region: string]: { [provider: number]: FormGroup } } } = {};
  loading: { [product: string]: boolean } = {};
  activeProviders: { [product: string]: { [region: string]: number } } = {};
  showPassword: { [key: string]: boolean } = {};
  collapsedRegions: { [index: number]: boolean } = {};

  private readonly settingPrefix = 'ProductCredentialSettingProvider';
  private settingValues: { [name: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private regionProviderMappingService: RegionProviderMappingService
  ) {}

  ngOnInit(): void {
    // Build the form map up front so the template never dereferences an empty map on the first
    // change-detection pass, then refill it once the authorized host endpoint answers.
    this.initializeForms();

    this.settingService.getProductCredentialSettings().subscribe({
      next: settings => {
        this.settingValues = {};
        (settings || []).forEach(setting => (this.settingValues[setting.settingName] = setting.settingValue));
        this.initializeForms();
      },
      error: () => {
        this.toastr.error(this.localizationService.instant('::Settings:ProductCredentialSetting:LoadFailed'));
      },
    });
  }

  getProvidersForRegion(regionValue: number): ProviderEnum[] {
    return this.regionProviderMappingService.getProvidersForRegion(regionValue);
  }

  getProviderName(provider: ProviderEnum): string {
    return ProviderEnum[provider];
  }

  getProviderDisplayName(provider: ProviderEnum): string {
    switch (provider) {
      case ProviderEnum.OracleProd:
        return 'Oracle Prod';
      case ProviderEnum.OraclePreProd:
        return 'Oracle Pre-prod';
      default:
        return ProviderEnum[provider];
    }
  }

  initializeForms(): void {
    this.products.forEach(product => {
      this.forms[product] = {};
      this.activeProviders[product] = {};
      
      this.regions.forEach(region => {
        this.forms[product][region.name] = {};
        const providers = this.getProvidersForRegion(region.value);
        
        // Set first provider as active by default
        this.activeProviders[product][region.name] = providers[0];
        
        providers.forEach(provider => {
          const enabledSettingName = this.buildConfigName(product, region.name, this.getProviderName(provider), 'Enabled');
          const urlSettingName = this.buildConfigName(product, region.name, this.getProviderName(provider), 'Url');
          const authEndpointSettingName = this.buildConfigName(product, region.name, this.getProviderName(provider), 'AuthEndpoint');
          const clientIdSettingName = this.buildConfigName(product, region.name, this.getProviderName(provider), 'ClientId');
          const clientSecretSettingName = this.buildConfigName(product, region.name, this.getProviderName(provider), 'ClientSecret');
    
          this.forms[product][region.name][provider] = this.fb.group({
            enabled: [this.getSettingValue(enabledSettingName)?.toLowerCase() === 'true'],
            url: [this.getSettingValue(urlSettingName), Validators.required],
            authEndpoint: [this.getSettingValue(authEndpointSettingName), Validators.required],
            clientId: [this.getSettingValue(clientIdSettingName), Validators.required],
            clientSecret: [this.getSettingValue(clientSecretSettingName) || ''],
          });
        });
      });
    });
  }

  setActiveProvider(product: string, region: string, provider: ProviderEnum): void {
    this.activeProviders[product][region] = provider;
  }

  isActiveProvider(product: string, region: string, provider: ProviderEnum): boolean {
    return this.activeProviders[product][region] === provider;
  }

  togglePasswordVisibility(key: string): void {
    this.showPassword[key] = !this.showPassword[key];
  }

  toggleRegionCollapse(index: number): void {
    this.collapsedRegions[index] = !this.collapsedRegions[index];
  }

  isRegionCollapsed(index: number): boolean {
    return this.collapsedRegions[index] || false;
  }

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.toastr.success(this.localizationService.instant('::CopiedToClipboard'));
    });
  }

  onSubmitForm(product: string): void {
    if (!this.isAllEnabledFormsValid(product)) return;
  
    this.loading[product] = true;
    const settings: ConfigurationSettingsDto[] = [];
  
    this.regions.forEach(region => {
      const providers = this.getProvidersForRegion(region.value);
      
      providers.forEach(provider => {
        const currentForm = this.forms[product][region.name][provider];
        const providerName = this.getProviderName(provider);
        
        // Only save settings that have been modified or enabled
        if (currentForm.dirty || currentForm.get('enabled').value) {
          settings.push({
            settingName: this.buildConfigName(product, region.name, providerName, 'Enabled'),
            settingValue: currentForm.get('enabled').value.toString()
          });
          
          if (currentForm.get('enabled').value) {
            // Only save non-empty values
            if (currentForm.get('url').value) {
              settings.push({ 
                settingName: this.buildConfigName(product, region.name, providerName, 'Url'), 
                settingValue: currentForm.get('url').value 
              });
            }
            
            if (currentForm.get('authEndpoint').value) {
              settings.push({ 
                settingName: this.buildConfigName(product, region.name, providerName, 'AuthEndpoint'), 
                settingValue: currentForm.get('authEndpoint').value 
              });
            }
            
            if (currentForm.get('clientId').value) {
              settings.push({ 
                settingName: this.buildConfigName(product, region.name, providerName, 'ClientId'), 
                settingValue: currentForm.get('clientId').value 
              });
            }
      
            if (currentForm.get('clientSecret').value) {
              settings.push({
                settingName: this.buildConfigName(product, region.name, providerName, 'ClientSecret'),
                settingValue: currentForm.get('clientSecret').value,
              });
            }
          }
        }
      });
    });

    // Only proceed if there are settings to save
    if (settings.length === 0) {
      this.toastr.info("No changes to save");
      this.loading[product] = false;
      return;
    }
  
    this.settingService.saveSetting(settings).subscribe({
      next: () => {
        this.toastr.success(this.localizationService.instant('AbpSettingManagement::SuccessfullySaved'));
        this.loading[product] = false;
        // Mark forms as pristine after successful save
        this.markFormsAsPristine(product);
      },
      error: () => {
        this.toastr.error(this.localizationService.instant('AbpSettingManagement::SaveFailed'));
        this.loading[product] = false;
      },
    });
  }

  private markFormsAsPristine(product: string): void {
    this.regions.forEach(region => {
      const providers = this.getProvidersForRegion(region.value);
      providers.forEach(provider => {
        this.forms[product][region.name][provider].markAsPristine();
      });
    });
  }

  private getSettingValue(name: string): string {
    return this.settingValues[name];
  }

  buildConfigName(product: string, region: string, provider: string, field: string): string {
    return `${this.settingPrefix}.${product}.${region}.${provider}.${field}`;
  }
  
  isAllEnabledFormsValid(product: string): boolean {
    return this.regions.every(region => {
      const providers = this.getProvidersForRegion(region.value);
      return providers
        .filter(provider => this.forms[product][region.name][provider].get('enabled').value)
        .every(provider => this.forms[product][region.name][provider].valid);
    });
  }
}
